import { c as _c } from "react/compiler-runtime";
import { flatten, unflatten } from "@lib/flatten";
import { ComponentBlocksCalculatorValues, ComponentBlocksRepaymentRateChange, ComponentBlocksSpecialRepayment, Maybe } from "@lib/generated/graphql";
import { useRouter } from "@lib/hooks/useRouter";
import { useCallback, useMemo, useRef } from "react";
import { DeepPartial } from "react-hook-form";
import { RepaymentCalculatorFormValue } from "./types";
const formatNumber = (value: number | undefined) => {
  return value ? Number(value.toFixed(2)).toString() : "";
};
const minifyValue = (value: DeepPartial<RepaymentCalculatorFormValue>, secondVariant: boolean) => {
  return secondVariant ? {
    l2: formatNumber(value.loanAmount),
    i2: formatNumber(value.interestRate),
    f2: formatNumber(value.fixedInterestPeriodYears),
    r12: formatNumber(value.repaymentRate),
    s12: formatNumber(value.secondaryRepaymentRate),
    s22: formatNumber(value.secondaryInterestRate),
    y2: value.yearlySpecialRepayment,
    s32: value.specialRepayments?.map(v => ({
      a: formatNumber(v?.amount),
      y: v?.year
    })),
    r22: value.repaymentRateChange?.map(v => ({
      r: formatNumber(v?.repaymentRate),
      y: v?.year
    }))
  } as const : {
    l: formatNumber(value.loanAmount),
    i: formatNumber(value.interestRate),
    f: formatNumber(value.fixedInterestPeriodYears),
    r1: formatNumber(value.repaymentRate),
    s1: formatNumber(value.secondaryRepaymentRate),
    s2: formatNumber(value.secondaryInterestRate),
    y: value.yearlySpecialRepayment,
    s3: value.specialRepayments?.map(v => ({
      a: formatNumber(v?.amount),
      y: v?.year
    })),
    r2: value.repaymentRateChange?.map(v => ({
      r: formatNumber(v?.repaymentRate),
      y: v?.year
    }))
  } as const;
};
const parseParams = (params: any): Partial<RepaymentCalculatorFormValue> => {
  const result: Partial<RepaymentCalculatorFormValue> = {};
  if (params.l) {
    result.loanAmount = Number(params.l);
  }
  if (params.f) {
    result.fixedInterestPeriodYears = Number(params.f);
  }
  if (params.i) {
    result.interestRate = Number(params.i);
  }
  if (params.r1) {
    result.repaymentRate = Number(params.r1);
  }
  if (params.s1) {
    result.secondaryRepaymentRate = Number(params.s1);
  }
  if (params.s2) {
    result.secondaryInterestRate = Number(params.s2);
  }
  if (params.y) {
    result.yearlySpecialRepayment = Number(params.y);
  }
  if (Array.isArray(params.s3)) {
    result.specialRepayments = params.s3.map(s => ({
      amount: Number(s.a),
      year: Number(s.y)
    }));
  }
  if (Array.isArray(params.r2)) {
    result.repaymentRateChange = params.r2.map(s => ({
      repaymentRate: Number(s.r),
      year: Number(s.y)
    }));
  }
  return result;
};
const parseParamsSecondVariant = (params: any): Partial<RepaymentCalculatorFormValue> => {
  const result: Partial<RepaymentCalculatorFormValue> = {};
  if (params.l2) {
    result.loanAmount = Number(params.l2);
  }
  if (params.f2) {
    result.fixedInterestPeriodYears = Number(params.f2);
  }
  if (params.i2) {
    result.interestRate = Number(params.i2);
  }
  if (params.r12) {
    result.repaymentRate = Number(params.r12);
  }
  if (params.s12) {
    result.secondaryRepaymentRate = Number(params.s12);
  }
  if (params.s22) {
    result.secondaryInterestRate = Number(params.s22);
  }
  if (params.y2) {
    result.yearlySpecialRepayment = Number(params.y2);
  }
  if (Array.isArray(params.s32)) {
    result.specialRepayments = params.s32.map(s => ({
      amount: Number(s.a),
      year: Number(s.y)
    }));
  }
  if (Array.isArray(params.r22)) {
    result.repaymentRateChange = params.r22.map(s => ({
      repaymentRate: Number(s.r),
      year: Number(s.y)
    }));
  }
  return result;
};
const defaultValues: RepaymentCalculatorFormValue = {
  loanAmount: 250000,
  fixedInterestPeriodYears: 15,
  interestRate: 4,
  repaymentRate: 2,
  secondaryRepaymentRate: 6.17,
  secondaryInterestRate: 2,
  yearlySpecialRepayment: 0,
  specialRepayments: [],
  repaymentRateChange: []
};
const withDefaultValues = (values?: Maybe<ComponentBlocksCalculatorValues>): RepaymentCalculatorFormValue => {
  const specialRepayments = (values?.specialRepayments || []).filter(Boolean) as ComponentBlocksSpecialRepayment[];
  const repaymentRateChange = (values?.repaymentRateChange || []).filter(Boolean) as ComponentBlocksRepaymentRateChange[];
  return {
    fixedInterestPeriodYears: values?.fixedInterestPeriodYears || defaultValues.fixedInterestPeriodYears,
    interestRate: values?.interestRate || defaultValues.interestRate,
    loanAmount: values?.loanAmount || defaultValues.loanAmount,
    repaymentRate: values?.repaymentRate || defaultValues.repaymentRate,
    secondaryInterestRate: values?.secondaryInterestRate || defaultValues.secondaryInterestRate,
    secondaryRepaymentRate: values?.secondaryRepaymentRate || defaultValues.secondaryRepaymentRate,
    yearlySpecialRepayment: values?.yearlySpecialRepayment || defaultValues.yearlySpecialRepayment,
    specialRepayments: specialRepayments.map(v => ({
      amount: v.amount,
      year: v.year
    })) || defaultValues.specialRepayments,
    repaymentRateChange: repaymentRateChange.map(v => ({
      repaymentRate: v.repaymentRate,
      year: v.year
    })) || defaultValues.repaymentRateChange
  };
};
const useRepaymentCalculatorParams = (_defaultValues, _defaultValuesSecondVariant) => {
  const $ = _c(27);
  let t0;
  if ($[0] !== _defaultValues) {
    t0 = withDefaultValues(_defaultValues);
    $[0] = _defaultValues;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const defaultValues = t0;
  let t1;
  if ($[2] !== _defaultValuesSecondVariant) {
    t1 = withDefaultValues(_defaultValuesSecondVariant);
    $[2] = _defaultValuesSecondVariant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const defaultValuesSecondVariant = t1;
  const router = useRouter();
  const timeout = useRef(undefined);
  let t2;
  if ($[4] !== router) {
    t2 = (value, valueSecondVariant) => {
      clearTimeout(timeout.current);
      timeout.current = window.setTimeout(() => {
        router.replace({
          query: {
            slug: router.query.slug,
            ...flatten(minifyValue(value, false)),
            ...flatten(minifyValue(valueSecondVariant, true))
          }
        }, undefined, {
          shallow: true
        });
      }, 1000);
    };
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const storeValuesToUrl = t2;
  let t3;
  bb0: {
    if (router.isReady) {
      let t4;
      if ($[6] !== router.query) {
        t4 = parseParams(unflatten(router.query as Record<string, string | number | boolean>));
        $[6] = router.query;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      const value_0 = t4;
      let t5;
      if ($[8] !== defaultValues || $[9] !== value_0) {
        t5 = {
          ...defaultValues,
          ...value_0
        };
        $[8] = defaultValues;
        $[9] = value_0;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      t3 = t5;
      break bb0;
    }
    t3 = undefined;
  }
  const urlValue = t3;
  let t4;
  bb1: {
    if (router.isReady) {
      let t5;
      if ($[11] !== router.query) {
        t5 = parseParamsSecondVariant(unflatten(router.query as Record<string, string | number | boolean>));
        $[11] = router.query;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      const value_1 = t5;
      let t6;
      if ($[13] !== value_1) {
        t6 = Object.keys(value_1);
        $[13] = value_1;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      const isNotDefault = t6.length > 0;
      let t7;
      if ($[15] !== defaultValuesSecondVariant || $[16] !== value_1) {
        t7 = {
          ...defaultValuesSecondVariant,
          ...value_1
        };
        $[15] = defaultValuesSecondVariant;
        $[16] = value_1;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      let t8;
      if ($[18] !== isNotDefault || $[19] !== t7) {
        t8 = [isNotDefault, t7];
        $[18] = isNotDefault;
        $[19] = t7;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      t4 = t8;
      break bb1;
    }
    let t5;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = [];
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t4 = t5;
  }
  const [isNotDefault_0, urlValueSecondVariant] = t4;
  let t5;
  if ($[22] !== isNotDefault_0 || $[23] !== storeValuesToUrl || $[24] !== urlValue || $[25] !== urlValueSecondVariant) {
    t5 = {
      urlValue,
      urlValueSecondVariant,
      storeValuesToUrl,
      isNotDefault: isNotDefault_0
    };
    $[22] = isNotDefault_0;
    $[23] = storeValuesToUrl;
    $[24] = urlValue;
    $[25] = urlValueSecondVariant;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
};
export default useRepaymentCalculatorParams;